// ============================================================
// src/hooks/useMeetings.ts
// Collections Firestore réelles (vérifiées dans GCP Console) :
//   meetings                ← collection racine existante ✅
//   meetingParticipants     ← à créer si absente
//   meetingTranscriptions   ← à créer si absente
// ============================================================

import { useState, useCallback } from 'react';
import {
  collection, doc, addDoc, getDoc, getDocs,
  updateDoc, query, where, orderBy,
  serverTimestamp, Timestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { invokeFunction, invokeFunctionSilent } from '@/lib/cloudrun';
import { useToast } from '@/hooks/use-toast';
import {
  DealMeeting, MeetingParticipant, MeetingTranscription,
  MeetingStatus, TranscriptionStatus, TranscriptionSegment,
  MeetingSummary, CreateMeetingRequest,
} from '@/types/meeting.types';

// ─────────────────────────────────────────────
// Noms des collections — modifiez ici si nécessaire
// ─────────────────────────────────────────────
const C = {
  meetings:       'meetings',              // ✅ existe dans votre Firestore
  participants:   'meetingParticipants',   // ⚠️ à créer si absente
  transcriptions: 'meetingTranscriptions', // ⚠️ à créer si absente
} as const;

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

const toDate = (val: any): Date | undefined => {
  if (!val) return undefined;
  if (val instanceof Timestamp) return val.toDate();
  if (val instanceof Date) return val;
  return new Date(val);
};

const docToMeeting = (id: string, d: any): DealMeeting => ({
  id, dealId: d.dealId, roomName: d.roomName, title: d.title,
  scheduledAt: toDate(d.scheduledAt), startedAt: toDate(d.startedAt),
  endedAt: toDate(d.endedAt), status: d.status as MeetingStatus,
  recordingEnabled: d.recordingEnabled, recordingUrl: d.recordingUrl,
  createdBy: d.createdBy,
  createdAt: toDate(d.createdAt) ?? new Date(),
  updatedAt: toDate(d.updatedAt) ?? new Date(),
});

const docToParticipant = (id: string, d: any): MeetingParticipant => ({
  id, meetingId: d.meetingId, userId: d.userId,
  userName: d.userName, userRole: d.userRole,
  joinedAt: toDate(d.joinedAt), leftAt: toDate(d.leftAt),
  consentGiven: d.consentGiven,
  createdAt: toDate(d.createdAt) ?? new Date(),
});

// ─────────────────────────────────────────────
// HOOK
// ─────────────────────────────────────────────

interface UseMeetingsReturn {
  meetings: DealMeeting[];
  currentMeeting: DealMeeting | null;
  participants: MeetingParticipant[];
  transcription: MeetingTranscription | null;
  isLoading: boolean;
  error: string | null;
  createMeeting: (request: CreateMeetingRequest) => Promise<DealMeeting | null>;
  joinMeeting: (meetingId: string, userName: string, userRole: string) => Promise<boolean>;
  leaveMeeting: (meetingId: string) => Promise<void>;
  startMeeting: (meetingId: string) => Promise<void>;
  endMeeting: (meetingId: string, audioBlob?: Blob) => Promise<void>;
  loadMeetings: (dealId?: string) => Promise<void>;
  loadTranscription: (meetingId: string) => Promise<void>;
  generateSummary: (meetingId: string) => Promise<void>;
}

export const useMeetings = (userId: string): UseMeetingsReturn => {
  const [meetings, setMeetings] = useState<DealMeeting[]>([]);
  const [currentMeeting, setCurrentMeeting] = useState<DealMeeting | null>(null);
  const [participants, setParticipants] = useState<MeetingParticipant[]>([]);
  const [transcription, setTranscription] = useState<MeetingTranscription | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const generateRoomName = (dealId: string): string => {
    const ts = Date.now().toString(36);
    const rnd = Math.random().toString(36).substring(2, 8);
    return `acquiz-deal-${dealId.substring(0, 8)}-${ts}-${rnd}`;
  };

  // ── Charger les réunions ─────────────────────────────────

  const loadMeetings = useCallback(async (dealId?: string) => {
    setIsLoading(true); setError(null);
    try {
      const ref = collection(db, C.meetings);
      const q = dealId
        ? query(ref, where('dealId', '==', dealId), orderBy('createdAt', 'desc'))
        : query(ref, orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      setMeetings(snap.docs.map(d => docToMeeting(d.id, d.data())));
    } catch (err: any) {
      console.error('Error loading meetings:', err);
      setError(err.message);
    } finally { setIsLoading(false); }
  }, []);

  // ── Créer une réunion ────────────────────────────────────

  const createMeeting = useCallback(async (
    request: CreateMeetingRequest
  ): Promise<DealMeeting | null> => {
    setIsLoading(true); setError(null);
    try {
      const roomName = generateRoomName(request.dealId);
      const now = serverTimestamp();

      const meetingRef = await addDoc(collection(db, C.meetings), {
        dealId: request.dealId, roomName, title: request.title,
        scheduledAt: request.scheduledAt ?? null,
        recordingEnabled: request.recordingEnabled ?? true,
        createdBy: userId,
        status: request.scheduledAt ? 'scheduled' : 'active',
        createdAt: now, updatedAt: now,
      });

      if (request.recordingEnabled !== false) {
        await addDoc(collection(db, C.transcriptions), {
          meetingId: meetingRef.id, status: 'pending',
          createdAt: now, updatedAt: now,
        });
      }

      const meeting: DealMeeting = {
        id: meetingRef.id, dealId: request.dealId, roomName,
        title: request.title, scheduledAt: request.scheduledAt,
        status: (request.scheduledAt ? 'scheduled' : 'active') as MeetingStatus,
        recordingEnabled: request.recordingEnabled ?? true,
        createdBy: userId, createdAt: new Date(), updatedAt: new Date(),
      };

      setMeetings(prev => [meeting, ...prev]);
      toast({ title: 'Meeting created', description: `Room: ${roomName}` });
      return meeting;
    } catch (err: any) {
      console.error('Error creating meeting:', err);
      setError(err.message);
      toast({ variant: 'destructive', title: 'Error', description: err.message });
      return null;
    } finally { setIsLoading(false); }
  }, [userId, toast]);

  // ── Rejoindre ────────────────────────────────────────────

  const joinMeeting = useCallback(async (
    meetingId: string, userName: string, userRole: string
  ): Promise<boolean> => {
    try {
      const now = serverTimestamp();
      await addDoc(collection(db, C.participants), {
        meetingId, userId, userName, userRole,
        joinedAt: now, consentGiven: true, createdAt: now,
      });
      const snap = await getDoc(doc(db, C.meetings, meetingId));
      if (snap.exists()) setCurrentMeeting(docToMeeting(snap.id, snap.data()));
      const pSnap = await getDocs(
        query(collection(db, C.participants), where('meetingId', '==', meetingId))
      );
      setParticipants(pSnap.docs.map(d => docToParticipant(d.id, d.data())));
      return true;
    } catch (err: any) {
      console.error('Error joining meeting:', err);
      setError(err.message);
      return false;
    }
  }, [userId]);

  // ── Quitter ──────────────────────────────────────────────

  const leaveMeeting = useCallback(async (meetingId: string) => {
    try {
      const snap = await getDocs(query(
        collection(db, C.participants),
        where('meetingId', '==', meetingId),
        where('userId', '==', userId)
      ));
      await Promise.all(snap.docs.map(d => updateDoc(d.ref, { leftAt: serverTimestamp() })));
      setCurrentMeeting(null); setParticipants([]);
    } catch (err: any) { console.error('Error leaving meeting:', err); }
  }, [userId]);

  // ── Démarrer ─────────────────────────────────────────────

  const startMeeting = useCallback(async (meetingId: string) => {
    try {
      await updateDoc(doc(db, C.meetings, meetingId), {
        status: 'active', startedAt: serverTimestamp(), updatedAt: serverTimestamp(),
      });
      setCurrentMeeting(prev =>
        prev ? { ...prev, status: 'active' as MeetingStatus, startedAt: new Date() } : null
      );
      toast({ title: 'Meeting started' });
    } catch (err: any) {
      toast({ variant: 'destructive', title: 'Error', description: err.message });
    }
  }, [toast]);

  // ── Terminer ─────────────────────────────────────────────

  const endMeeting = useCallback(async (meetingId: string, audioBlob?: Blob) => {
    try {
      await updateDoc(doc(db, C.meetings, meetingId), {
        status: 'ended', endedAt: serverTimestamp(), updatedAt: serverTimestamp(),
      });

      if (audioBlob) {
        toast({ title: 'Processing transcription...', description: 'This may take a few minutes.' });
        const reader = new FileReader();
        reader.onloadend = async () => {
          const base64Audio = (reader.result as string).split(',')[1];
          const { error: transcribeError } = await invokeFunction(
            'transcribe-meeting', { meetingId, audioBase64: base64Audio }
          );
          if (transcribeError) {
            toast({ variant: 'destructive', title: 'Transcription failed', description: transcribeError.message });
          } else {
            toast({ title: 'Transcription completed' });
            await invokeFunctionSilent('generate-meeting-summary', { meetingId });
            toast({ title: 'Summary generated' });
          }
        };
        reader.readAsDataURL(audioBlob);
      }

      setCurrentMeeting(null); setParticipants([]);
      toast({ title: 'Meeting ended' });
    } catch (err: any) {
      toast({ variant: 'destructive', title: 'Error', description: err.message });
    }
  }, [toast]);

  // ── Transcription ────────────────────────────────────────

  const loadTranscription = useCallback(async (meetingId: string) => {
    try {
      const snap = await getDocs(
        query(collection(db, C.transcriptions), where('meetingId', '==', meetingId))
      );
      if (!snap.empty) {
        const d = snap.docs[0].data();
        setTranscription({
          id: snap.docs[0].id, meetingId: d.meetingId,
          audioUrl: d.audioUrl ?? undefined,
          transcriptionText: d.transcriptionText ?? undefined,
          transcriptionSegments: d.transcriptionSegments as TranscriptionSegment[] | undefined,
          summary: d.summary as MeetingSummary | undefined,
          status: d.status as TranscriptionStatus,
          language: d.language || 'fr',
          durationSeconds: d.durationSeconds ?? undefined,
          wordCount: d.wordCount ?? undefined,
          processingStartedAt: toDate(d.processingStartedAt),
          processingCompletedAt: toDate(d.processingCompletedAt),
          errorMessage: d.errorMessage ?? undefined,
          createdAt: toDate(d.createdAt) ?? new Date(),
          updatedAt: toDate(d.updatedAt) ?? new Date(),
        });
      } else { setTranscription(null); }
    } catch (err: any) { console.error('Error loading transcription:', err); }
  }, []);

  // ── Résumé ───────────────────────────────────────────────

  const generateSummary = useCallback(async (meetingId: string) => {
    try {
      toast({ title: 'Generating summary...' });
      const { error: summaryError } = await invokeFunction('generate-meeting-summary', { meetingId });
      if (summaryError) throw summaryError;
      await loadTranscription(meetingId);
      toast({ title: 'Summary generated successfully' });
    } catch (err: any) {
      toast({ variant: 'destructive', title: 'Error', description: err.message });
    }
  }, [loadTranscription, toast]);

  return {
    meetings, currentMeeting, participants, transcription,
    isLoading, error, createMeeting, joinMeeting, leaveMeeting,
    startMeeting, endMeeting, loadMeetings, loadTranscription, generateSummary,
  };
};